import { FormattedMessage } from '@alltrails/shared/react-intl';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import CurrencyCode from 'types/CurrencyCode';
import FormattedPercent from 'components/FormattedPercent';
import Plan from 'types/Plan';
import Key from 'types/Key';
import Coupon from 'types/Coupon';
import { getPriceTotal, isTrial } from 'utils/PlanUtils';
import Typography from 'components/Typography';
import * as styles from './PlanTypeCard.module.scss';

type Props = {
  plan: Plan;
  onClick?: (event?: React.MouseEvent) => void;
  isActive?: boolean;
  currencyCode: CurrencyCode;
  isCouponApplied?: boolean;
  staticCard?: boolean;
  extraClasses?: string;
  coupon?: Coupon;
  billedAtOnce?: boolean;
};

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=586%3A30993
// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=4500%3A91389
const PlanTypeCard = ({
  billedAtOnce,
  plan,
  onClick,
  isActive = false,
  currencyCode,
  isCouponApplied = false,
  coupon,
  staticCard,
  extraClasses
}: Props) => {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  const formatterNoDecimals = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  });

  const enterKeyHandler = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === Key.Enter) {
      onClick();
    }
  };

  // eslint-disable-next-line prefer-destructuring
  let priceTotal: number | string = plan.priceTotal;
  // eslint-disable-next-line prefer-destructuring
  let priceMonth: number | string = plan.priceMonth;
  let { showDiscount } = plan;
  // eslint-disable-next-line prefer-destructuring
  let discountAmount: string | JSX.Element = plan.discountAmount;

  if (isCouponApplied && coupon && plan.allowCoupon) {
    showDiscount = true;
    discountAmount = (
      <FormattedMessage
        defaultMessage="{percent} off"
        values={{
          percent: <FormattedPercent value={coupon.discountPercentage / 100} />
        }}
      />
    );
    priceTotal = getPriceTotal(plan, coupon);
    priceMonth = priceTotal / 12;
  } else if (plan.discountOverride) {
    showDiscount = true;
    discountAmount = plan.discountOverrideAmount;
  }

  const perMonth = formatter.format(Number(priceMonth));
  const total = formatter.format(Number(priceTotal));

  function getPlanDetails() {
    if (billedAtOnce) {
      return {
        billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed once at {total}" values={{ perMonth, total }} />,
        label: <FormattedMessage defaultMessage="Annual plan" />
      };
    }

    if (isTrial(plan)) {
      return {
        billedAt: (
          <FormattedMessage
            defaultMessage="Then {perMonth}/month - billed annually at {total}. Pay {trialCost} today."
            values={{ perMonth, total, trialCost: formatterNoDecimals.format(0) }}
          />
        ),
        label: <FormattedMessage defaultMessage="First 7 days free" />
      };
    }

    return {
      billedAt: <FormattedMessage defaultMessage="{perMonth}/month - billed annually at {total}" values={{ perMonth, total }} />,
      label: <FormattedMessage defaultMessage="Annual plan" />
    };
  }

  const planDetail = getPlanDetails();

  if (!planDetail) {
    throw new Error(`No plan detail found for recurly plan ${plan.recurlyPlanCode}`);
  }

  let discount;
  let billedAtLang = planDetail.billedAt;

  if (showDiscount) {
    // billing language is slightly different when discounting first year
    discount = <div className={styles.discount}>{discountAmount}</div>;

    if (['alltrails_pro_annual_30', 'alltrails_pro_annual_36'].includes(plan.recurlyPlanCode)) {
      billedAtLang = <FormattedMessage defaultMessage="{perMonth}/month - 1st year billed at {total}" values={{ perMonth, total }} />;
    }
  }

  return (
    <div
      tabIndex={staticCard ? -1 : 0}
      onClick={onClick}
      onKeyPress={enterKeyHandler}
      role="button"
      className={`${isActive ? styles.active : ''} ${!onClick ? styles.staticCard : ''} ${styles.button} ${styles.card} ${extraClasses}`}
    >
      <div className={styles.contents}>
        <div className={styles.label}>
          <Typography component="div" variant="heading200">
            {planDetail.label}
          </Typography>
          {discount}
        </div>
        <Typography component="div" variant="text200">
          {billedAtLang}
        </Typography>
      </div>
    </div>
  );
};

export default PlanTypeCard;
